import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getUserCvsTable } from "@/lib/userCvsTable";
import { logError } from "@/lib/logger";

export interface UserCV {
  id: string;
  user_id: string;
  file_path: string;
  display_name: string;
  uploaded_at: string;
  role: string | null;
  language: "sv" | "en" | "both";
  is_default: boolean;
  extracted_skills?: string[];
  analyzed_at?: string;
}

export type JobLanguage = "sv" | "en" | null;

/**
 * Hook for managing multiple CVs with role associations
 * Replaces the old language-only approach (cv_file_path_sv/en)
 */
export function useMultiCV() {
  const [cvs, setCvs] = useState<UserCV[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCVs = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setLoading(false);
        return;
      }

      const { data, error } = await getUserCvsTable()
        .select("*")
        .eq("user_id", session.user.id)
        .order("is_default", { ascending: false })
        .order("uploaded_at", { ascending: false });

      if (error) throw error;
      setCvs((data as unknown as UserCV[]) || []);
    } catch (error) {
      logError("Error fetching CVs", error);
      setCvs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCVs();
  }, [fetchCVs]);

  const hasCV = cvs.length > 0;

  /**
   * Get best CV for a job based on role and language
   * Priority:
   * 1. Role-specific CV matching job role + language
   * 2. Role-specific CV matching job role (any language)
   * 3. Language-specific CV (no role match)
   * 4. Default CV
   * 5. Any CV as fallback
   */
  const getBestCV = (
    jobRole: string | null,
    preferredLang: JobLanguage
  ): { path: string; displayName: string; usedRole: string | null; usedLang: "sv" | "en" | "both"; isFallback: boolean } | null => {
    if (cvs.length === 0) return null;

    // Normalize job role to match CV role format (e.g., "ux-designer" -> "ux-designer")
    const normalizedJobRole = jobRole
      ? jobRole.toLowerCase().replace(/\s+/g, "-").replace(/_/g, "-")
      : null;

    // 1. Try to find role-specific CV matching both role and language
    if (normalizedJobRole && preferredLang) {
      const roleLangMatch = cvs.find(
        (cv) =>
          cv.role === normalizedJobRole &&
          (cv.language === preferredLang || cv.language === "both")
      );
      if (roleLangMatch) {
        return {
          path: roleLangMatch.file_path,
          displayName: roleLangMatch.display_name,
          usedRole: roleLangMatch.role,
          usedLang: roleLangMatch.language,
          isFallback: false,
        };
      }
    }

    // 2. Try to find role-specific CV (any language)
    if (normalizedJobRole) {
      const roleMatch = cvs.find((cv) => cv.role === normalizedJobRole);
      if (roleMatch) {
        return {
          path: roleMatch.file_path,
          displayName: roleMatch.display_name,
          usedRole: roleMatch.role,
          usedLang: roleMatch.language,
          isFallback: false,
        };
      }
    }

    // 3. Try to find language-specific CV (no role match)
    if (preferredLang) {
      const langMatch = cvs.find(
        (cv) => cv.language === preferredLang || cv.language === "both"
      );
      if (langMatch) {
        return {
          path: langMatch.file_path,
          displayName: langMatch.display_name,
          usedRole: langMatch.role,
          usedLang: langMatch.language,
          isFallback: true,
        };
      }
    }

    // 4. Use default CV
    const defaultCV = cvs.find((cv) => cv.is_default);
    if (defaultCV) {
      return {
        path: defaultCV.file_path,
        displayName: defaultCV.display_name,
        usedRole: defaultCV.role,
        usedLang: defaultCV.language,
        isFallback: true,
      };
    }

    // 5. Fallback to any CV
    const anyCV = cvs[0];
    if (anyCV) {
      return {
        path: anyCV.file_path,
        displayName: anyCV.display_name,
        usedRole: anyCV.role,
        usedLang: anyCV.language,
        isFallback: true,
      };
    }

    return null;
  };

  return {
    cvs,
    loading,
    hasCV,
    getBestCV,
    refetch: fetchCVs,
  };
}
